/**
 * Settings / QoL modal (GAM-25): notation, particle quality, autosave cadence,
 * accessibility toggles, and save-slot management with export / import front and
 * centre — plus the data-driven achievements list. Pure presentation like the rest
 * of `@ui` (layer rules: no `@persistence` import): the app hands in a view model
 * and receives typed action callbacks.
 */
import { tagComponent } from './debugLabel.js';
import { button, el } from './dom.js';

export type NotationSetting = 'letters' | 'scientific';
export type ParticleQualitySetting = 'off' | 'low' | 'medium' | 'high';

export interface SettingsViewModel {
  readonly notation: NotationSetting;
  readonly particleQuality: ParticleQualitySetting;
  readonly autosaveInterval: number;
  readonly reducedMotion: boolean;
  readonly highContrast: boolean;
  readonly activeSlot: string;
  readonly slots: readonly string[];
}

export interface AchievementView {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly unlocked: boolean;
}

export interface SettingsActions {
  /** Persist a partial settings update (the app applies side effects live). */
  readonly update: (change: Partial<SettingsViewModel>) => void;
  /** Save the current slot, switch to `slot`, and reload its state. */
  readonly switchSlot: (slot: string) => Promise<void>;
  /** Base64 transfer code for the active slot. */
  readonly exportSave: () => Promise<string>;
  /** Import a transfer code into the active slot; resolves false on a bad code. */
  readonly importSave: (code: string) => Promise<boolean>;
  /** Wipe the active slot back to a fresh start. Irreversible. */
  readonly resetSave: () => Promise<void>;
  /** Restore every preference to its default (the active save is untouched). */
  readonly resetSettings: () => void;
  /** Save, drop cached app files, and reload. Settings and the save survive. */
  readonly hardReload: () => Promise<void>;
}

function labelled(label: string, hint: string, control: HTMLElement): HTMLElement {
  const row = el('label', { class: 'settings-field' }, [
    el('span', { class: 'settings-label', text: label }),
    el('span', { class: 'settings-hint', text: hint }),
  ]);
  row.append(control);
  return row;
}

function select(
  ariaLabel: string,
  options: readonly { value: string; label: string }[],
  current: string,
  onChange: (value: string) => void,
): HTMLSelectElement {
  const node = el('select', { ariaLabel });
  for (const option of options) {
    const o = document.createElement('option');
    o.value = option.value;
    o.textContent = option.label;
    o.selected = option.value === current;
    node.append(o);
  }
  node.addEventListener('change', () => {
    onChange(node.value);
  });
  return node;
}

function toggle(
  label: string,
  hint: string,
  checked: boolean,
  onChange: (value: boolean) => void,
): HTMLElement {
  const box = document.createElement('input');
  box.type = 'checkbox';
  box.checked = checked;
  box.setAttribute('aria-label', label);
  box.addEventListener('change', () => {
    onChange(box.checked);
  });
  return labelled(label, hint, box);
}

/**
 * A destructive action button that requires two clicks: the first arms it
 * (label flips to `confirmLabel` for a few seconds), the second actually
 * fires `onConfirm`. Arming lapses back to the resting label on its own —
 * no modal-on-modal confirm dialog needed for a single irreversible action.
 */
function dangerButton(
  label: string,
  confirmLabel: string,
  onConfirm: () => void,
  testid: string,
): HTMLButtonElement {
  let armTimer = 0;
  const disarm = (): void => {
    window.clearTimeout(armTimer);
    btn.textContent = label;
    btn.classList.remove('armed');
  };
  const btn = button(
    label,
    () => {
      if (!btn.classList.contains('armed')) {
        btn.textContent = confirmLabel;
        btn.classList.add('armed');
        armTimer = window.setTimeout(disarm, 4000);
        return;
      }
      disarm();
      onConfirm();
    },
    { class: 'danger', testid },
  );
  return btn;
}

function trapFocus(event: KeyboardEvent, dialog: HTMLElement): void {
  const focusables = Array.from(
    dialog.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((node) => !node.hasAttribute('disabled'));
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Mount the settings modal. Keyboard-accessible: focus is trapped, Escape or a
 * backdrop click closes, and focus returns to the previously focused element.
 */
export function showSettingsModal(
  mount: HTMLElement,
  vm: SettingsViewModel,
  achievements: readonly AchievementView[],
  actions: SettingsActions,
): { close: () => void } {
  const previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  const backdrop = el('div', { class: 'settings-backdrop', testid: 'settings-modal' });
  const dialog = el('div', { class: 'settings-dialog' });
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-label', 'Settings');
  tagComponent(dialog, 'settings-dialog');

  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    backdrop.remove();
    previouslyFocused?.focus();
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      close();
    } else if (event.key === 'Tab') {
      trapFocus(event, dialog);
    }
  };
  document.addEventListener('keydown', onKey);
  backdrop.addEventListener('mousedown', (event) => {
    if (event.target === backdrop) close();
  });

  dialog.append(el('h2', { text: 'Settings' }));

  dialog.append(
    labelled(
      'Number notation',
      'How large numbers are displayed.',
      select(
        'Number notation',
        [
          { value: 'letters', label: 'Letters (1.23 M)' },
          { value: 'scientific', label: 'Scientific (1.23e6)' },
        ],
        vm.notation,
        (value) => {
          actions.update({ notation: value as NotationSetting });
        },
      ),
    ),
    labelled(
      'Particle quality',
      'Lower this if the world view stutters.',
      select(
        'Particle quality',
        (['off', 'low', 'medium', 'high'] as const).map((tier) => ({
          value: tier,
          label: tier,
        })),
        vm.particleQuality,
        (value) => {
          actions.update({ particleQuality: value as ParticleQualitySetting });
        },
      ),
    ),
  );

  const autosave = document.createElement('input');
  autosave.type = 'number';
  autosave.min = '5';
  autosave.max = '300';
  autosave.step = '5';
  autosave.value = String(vm.autosaveInterval);
  autosave.setAttribute('aria-label', 'Autosave interval seconds');
  autosave.addEventListener('change', () => {
    const clamped = Math.max(5, Math.min(300, Number(autosave.value) || 30));
    autosave.value = String(clamped);
    actions.update({ autosaveInterval: clamped });
  });
  dialog.append(
    labelled(
      'Autosave interval (s)',
      'How often progress is written to storage.',
      autosave,
    ),
    toggle(
      'Reduced motion',
      'Disables particles and drifting animation.',
      vm.reducedMotion,
      (value) => {
        actions.update({ reducedMotion: value });
      },
    ),
    toggle(
      'High contrast',
      'Stronger borders and text contrast.',
      vm.highContrast,
      (value) => {
        actions.update({ highContrast: value });
      },
    ),
    labelled(
      'Reset settings',
      'Restores every preference above to its default. The active save is untouched.',
      dangerButton(
        'Restore defaults',
        'Confirm?',
        actions.resetSettings,
        'reset-settings',
      ),
    ),
  );

  // ---- save management: slots + export/import front and centre --------------
  dialog.append(el('h2', { text: 'Save management' }));
  dialog.append(
    labelled(
      'Active slot',
      'Switch between three independent saves.',
      select(
        'Active save slot',
        vm.slots.map((slot) => ({ value: slot, label: slot.replace('slot-', 'Slot ') })),
        vm.activeSlot,
        (value) => {
          void actions.switchSlot(value).then(close);
        },
      ),
    ),
  );

  const transferBox = document.createElement('textarea');
  transferBox.rows = 3;
  transferBox.placeholder = 'Transfer code appears here on export; paste one to import.';
  transferBox.setAttribute('aria-label', 'Save transfer code');
  transferBox.dataset.testid = 'transfer-code';

  const status = el('p', { class: 'settings-hint', testid: 'transfer-status' });
  const exportButton = button(
    'Export save',
    () => {
      void actions.exportSave().then((code) => {
        transferBox.value = code;
        void navigator.clipboard.writeText(code).catch(() => undefined);
        status.textContent = 'Exported — code copied to the clipboard and the box above.';
      });
    },
    { class: 'primary', testid: 'export-save' },
  );
  const importButton = button(
    'Import save',
    () => {
      void actions.importSave(transferBox.value.trim()).then((ok) => {
        status.textContent = ok
          ? 'Imported — save loaded into the active slot.'
          : 'That code could not be read; the current save is untouched.';
        if (ok) close();
      });
    },
    { testid: 'import-save' },
  );
  dialog.append(
    transferBox,
    el('div', { class: 'settings-actions' }, [exportButton, importButton]),
    status,
  );

  const resetSaveButton = dangerButton(
    'Wipe save',
    'Confirm?',
    () => {
      void actions.resetSave().then(close);
    },
    'reset-save',
  );
  dialog.append(
    labelled(
      'Reset save',
      `Wipes ${vm.activeSlot.replace('slot-', 'Slot ')} back to a fresh start. This cannot be undone.`,
      resetSaveButton,
    ),
  );

  const hardReloadButton = button(
    'Reload app',
    () => {
      hardReloadButton.disabled = true;
      hardReloadButton.textContent = 'Reloading…';
      void actions.hardReload();
    },
    { class: 'buy', testid: 'hard-reload' },
  );
  dialog.append(
    labelled(
      'Having display or update problems?',
      'Saves, then clears cached app files and reloads the latest version. Your save and settings are untouched.',
      hardReloadButton,
    ),
  );

  // ---- achievements (data-driven, milestone vocabulary) ----------------------
  dialog.append(el('h2', { text: 'Achievements' }));
  const list = el('ul', { class: 'achievement-list', testid: 'achievement-list' });
  for (const achievement of achievements) {
    list.append(
      el(
        'li',
        {
          class: achievement.unlocked ? 'achievement unlocked' : 'achievement locked',
          title: achievement.description,
        },
        [
          el('strong', { text: achievement.name }),
          el('span', {
            class: 'settings-hint',
            text: achievement.unlocked ? achievement.description : '???',
          }),
        ],
      ),
    );
  }
  dialog.append(list);

  dialog.append(button('Close', close, { class: 'primary', testid: 'settings-close' }));
  backdrop.append(dialog);
  mount.append(backdrop);
  dialog.querySelector<HTMLElement>('select, input, button')?.focus();
  return { close };
}
